import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TaskForm from "./TaskForm";
import TaskDetailsPage from "./tasks/TaskDetailsPage";
import { useUser } from "../context/UserContext";
import CustomSelect from "./common/CustomSelect";
import { useNotification } from "../context/NotificationContext";
import { supabase } from "../supabaseClient";

const parseDate = (iso) => {
  if (!iso) return null;
  const dateStr = String(iso).slice(0, 10);
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  return new Date(y, m - 1, d);
};

const toIsoDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};


function CalendarView({
  tasks = [],
  teams = [],
  subTeams = [],
  users = [],
  onUpdateTask,
  onDeleteTask,
  onCreateTask,
  onRefreshUsers,
  onRefreshTasks,
  darkMode
}) {
  const { showAlert } = useNotification();
  const { currentUser } = useUser();
  const navigate = useNavigate();

  const isGlobalAdmin = currentUser?.role === "Global Admin";
  const isOperationalAdmin = currentUser?.role === "Operational Admin";
  const isDeptAdmin = currentUser?.role === "Department Admin" || (currentUser?.adminDeptIds && currentUser.adminDeptIds.length > 0);
  const isAdmin = isGlobalAdmin || isOperationalAdmin || isDeptAdmin;
  const isUser = currentUser?.role === "User";

  const getTaskAssignees = useCallback((task) => {
    if (Array.isArray(task?.task_assignees) && task.task_assignees.length > 0) {
      return task.task_assignees
        .map((a) => {
          const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles;
          const matchedUser = users.find((u) => u.id === a.user_id);
          return profile || matchedUser || null;
        })
        .filter(Boolean);
    }
    return [];
  }, [users]);

  const isAssignedToCurrentUser = useCallback((task) => {
    return getTaskAssignees(task).some(
      (u) =>
        u.id === currentUser?.id ||
        u.email?.toLowerCase() === currentUser?.email?.toLowerCase()
    );
  }, [getTaskAssignees, currentUser]);

  const getAssigneeDisplay = useCallback((task) => {
    const assignees = getTaskAssignees(task);
    return assignees
      .map((u) => u.name || u.email)
      .filter(Boolean)
      .join(", ");
  }, [getTaskAssignees]);

  const today = useMemo(() => new Date(), []);

  const [monthValue, setMonthValue] = useState(() =>
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`
  );

  const [filterScope, setFilterScope] = useState(
    isGlobalAdmin || isOperationalAdmin || isDeptAdmin ? "all" : "my"
  );
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFilter, setDateFilter] = useState("month");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDateInfo, setSelectedDateInfo] = useState(null);
  const [selectedWeekInfo, setSelectedWeekInfo] = useState(null);

  const isTaskInMyTeam = (t) => {
    if (!t) return false;
    const allowedTeamIds = [
      currentUser?.team_id,
      ...(currentUser?.memberships || []).map((m) => m.team_id)
    ].filter(Boolean);
    return allowedTeamIds.includes(t.team_id);
  };



  const uploadAttachmentFiles = async ({ files = [], folder, taskId }) => {
    const uploaded = [];

    for (const file of files || []) {
      if (!file) continue;

      if (file.url || file.publicUrl) {
        uploaded.push(file);
        continue;
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `${folder}/${taskId}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("attachments")
        .getPublicUrl(filePath);

      uploaded.push({
        name: file.name,
        url: data.publicUrl,
        size: file.size,
        type: file.type || "file",
        path: filePath,
        uploaded_by: currentUser?.id || null,
        uploaded_at: new Date().toISOString(),
      });
    }

    return uploaded;
  };

  const addTaskChildrenAndNotify = async ({ taskId, payload, cleanAssignedUsers, cleanUserCases }) => {
    if (!taskId) return;

    console.log("INSIDE addTaskChildrenAndNotify");
    console.log("taskId:", taskId);
    console.log("cleanAssignedUsers:", cleanAssignedUsers);

    if (!cleanAssignedUsers || cleanAssignedUsers.length === 0) {
      console.error("No assignees found before task_assignees insert");
      showAlert("Task saved, but no assignee was selected.");
      return;
    }

    const assigneeRows = cleanAssignedUsers.map((user) => ({
      task_id: taskId,
      user_id: user.id,
      assigned_by: currentUser?.id || null,
    }));

    console.log("task_assignee rows to insert:", assigneeRows);

    const { data: insertedAssignees, error: assigneeInsertError } = await supabase
      .from("task_assignees")
      .insert(assigneeRows)
      .select();

    if (assigneeInsertError) {
      console.error("task_assignees insert failed:", assigneeInsertError);
      showAlert(
        "Task saved, but assignee insert failed: " +
          assigneeInsertError.message
      );
      return;
    }

    console.log("task_assignees inserted:", insertedAssignees);

    if (cleanUserCases.length > 0) {
      const userCaseRows = [];

      for (const uc of cleanUserCases) {
        const uploadedFiles = await uploadAttachmentFiles({
          files: uc.files || [],
          folder: "user-cases",
          taskId,
        });

        userCaseRows.push({
          task_id: taskId,
          title: uc.title || "Untitled User Story",
          description: uc.description || null,
          acceptance_criteria: uc.acceptanceCriteria || null,
          test_cases: (uc.testCases || []).map((t) => ({
            title: t,
            status: "Pending",
          })),
          test_cases_count: (uc.testCases || []).length,
          attachments: uploadedFiles,
          status: "Pending",
          created_by: currentUser?.id || null,
        });
      }

      if (userCaseRows.length > 0) {
        await supabase.from("task_user_cases").insert(userCaseRows);
      }
    }

    if (cleanAssignedUsers.length > 0) {
      await supabase.from("notifications").insert(
        cleanAssignedUsers.map((user) => ({
          user_id: user.id,
          task_id: taskId,
          title: "Task assigned",
          message: payload.task_note || `You have been assigned "${payload.title}"`,
          type: "TASK_CREATED",
          action_role: "assignee",
          attachments: payload.attachments || [],
          is_read: false,
          is_dismissed: false,
        }))
      );

      const emailUsers = cleanAssignedUsers.filter((u) => u.email);

      if (emailUsers.length > 0) {
        await fetch("/api/email/task-created", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
          body: JSON.stringify({
            users: emailUsers,
            task: {
              id: taskId,
              title: payload.title,
              description: payload.task_note || payload.description,
              assigned_to: cleanAssignedUsers.map((u) => u.name || u.email).join(", "),
              assigned_by: currentUser?.name,
              start_date: payload.start_date,
              due_date: payload.due_date,
              priority: payload.priority,
              attachments: payload.attachments || [],
            },
            taskUrl: `${window.location.origin}/tasks/${taskId}`,
          }),
        });
      }
    }
  };





  const normalizedTasks = useMemo(() => {
    let filtered = tasks || [];

    filtered = filtered.filter((t) => {
      if (t.deleted_at) return false;
      let matchesScope = false;

      if (filterScope === "my") {
        matchesScope = isAssignedToCurrentUser(t);
      } else if (filterScope === "team") {
        if (t.team_id === selectedTeamId) {
          matchesScope = true;
        } else {
          const assignees = getTaskAssignees(t);
          matchesScope = assignees.some((u) => u.team_id === selectedTeamId);
        }
      } else if (filterScope === "all") {
        if (isGlobalAdmin || isOperationalAdmin) {
          matchesScope = true;
        } else if (isDeptAdmin) {
          const adminDeptIds = currentUser?.adminDeptIds || [];
          const allowedTeamIds = currentUser?.allowedTeamIds || [];
          const taskTeam = teams.find((tm) => tm.id === t.team_id);
          const inAdminDept = taskTeam && adminDeptIds.includes(taskTeam.department_id);
          const assignees = getTaskAssignees(t);
          const inMyTeam = allowedTeamIds.includes(t.team_id) ||
            assignees.some((u) => allowedTeamIds.includes(u.team_id));
          matchesScope = inAdminDept || inMyTeam;
        } else {
          const allowedTeamIds = currentUser?.allowedTeamIds || [];
          matchesScope = isAssignedToCurrentUser(t) || allowedTeamIds.includes(t.team_id);
        }
      }

      return matchesScope;
    });

    if (filterStatus !== "all") {
      filtered = filtered.filter((t) => (t.status || "Todo") === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((t) => {
        const title = (t.title || "").toLowerCase();
        const assigneeName = getTaskAssignees(t)
          .map((u) => u.name || u.email)
          .join(", ")
          .toLowerCase();

        return title.includes(query) || assigneeName.includes(query);
      });
    }

    // Exclude templates from direct display
    const nonTemplates = filtered.filter(t => t.task_type !== "loop" || t.is_loop_occurrence);
    const combinedTasks = [...nonTemplates];

    const dedupedMap = new Map();
    combinedTasks.forEach(t => {
      if (t.is_loop_occurrence || t.task_type === "loop") {
        const parentId = t.loop_series_id || t.parent_task_id || t.id;
        const dateKey = t.start_date ? String(t.start_date).slice(0, 10) : "nodate";
        const key = `${parentId}_${dateKey}`;
        if (!dedupedMap.has(key)) {
          dedupedMap.set(key, t);
        }
      } else {
        dedupedMap.set(t.id, t);
      }
    });

    const dedupedTasks = Array.from(dedupedMap.values());

    return dedupedTasks.map((t) => {
      const sd =
        t.startDate || t.start_date || t.dueDate || t.due_date || null;
      const dd =
        t.dueDate || t.due_date || t.startDate || t.start_date || null;

      const start = parseDate(sd);
      const due = parseDate(dd);

      const extDates = t.extended_due_dates || [];

      let ext1 = extDates[0]
        ? parseDate(extDates[0])
        : t.extended_due_date_1
        ? parseDate(t.extended_due_date_1)
        : null;

      let ext2 = extDates[1]
        ? parseDate(extDates[1])
        : t.extended_due_date_2
        ? parseDate(t.extended_due_date_2)
        : null;

      if (due && ext1 && ext1 <= due) ext1 = null;
      if (due && ext2 && ext2 <= due) ext2 = null;

      let color = t.is_preview ? "#9ca3af" : "#3b82f6";
      if (!t.is_preview) {
        if (t.priority === "High") color = "#fe6969ff";
        else if (t.priority === "Medium") color = "#f0b88fff";
        else if (t.priority === "Low") color = "#89f7b1ff";
        if (t.status === "Completed") color = "#3e81ecff";
      }

      let effectiveEnd = due;
      if (ext1 && (!effectiveEnd || ext1 > effectiveEnd)) effectiveEnd = ext1;
      if (ext2 && (!effectiveEnd || ext2 > effectiveEnd)) effectiveEnd = ext2;

      return {
        ...t,
        _start: start,
        _due: due,
        _ext1: ext1,
        _ext2: ext2,
        _effectiveEnd: effectiveEnd,
        _color: color
      };
    });
  }, [
    tasks,
    currentUser,
    filterScope,
    selectedTeamId,
    filterStatus,
    searchQuery,
    isGlobalAdmin,
    isOperationalAdmin,
    isDeptAdmin,
    getTaskAssignees,
    isAssignedToCurrentUser,
    teams
  ]);

  const { monthLabel, monthDates } = useMemo(() => {
    const [yStr, mStr] = monthValue.split("-");
    const year = Number(yStr);
    const month = Number(mStr) - 1;

    const firstOfMonth = new Date(year, month, 1);
    const startCell = new Date(firstOfMonth);
    startCell.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());

    const dates = [];

    for (let i = 0; i < 42; i++) {
      const d = new Date(startCell);
      d.setDate(startCell.getDate() + i);

      dates.push({
        date: d,
        iso: toIsoDate(d),
        inMonth: d.getMonth() === month
      });
    }

    let finalDates = dates;
    const lastRow = dates.slice(35);
    const allNextMonth = lastRow.every((d) => !d.inMonth);

    if (allNextMonth) {
      finalDates = dates.slice(0, 35);
    }

    const label = firstOfMonth.toLocaleString(undefined, {
      month: "long",
      year: "numeric"
    });

    return { monthLabel: label, monthDates: finalDates };
  }, [monthValue]);

  const tasksByDate = useMemo(() => {
    const map = {};

    normalizedTasks.forEach((task) => {
      const start = task._start || task._due;
      const end = task._effectiveEnd || task._due || task._start;

      if (!start || !end) return;

      const current = new Date(start);
      current.setHours(0, 0, 0, 0);

      const last = new Date(end);
      last.setHours(0, 0, 0, 0);

      while (current <= last) {
        const iso = toIsoDate(current);
        if (!map[iso]) map[iso] = [];
        map[iso].push(task);
        current.setDate(current.getDate() + 1);
      }
    });

    return map;
  }, [normalizedTasks]);

  const visibleTasksByDate = useMemo(() => {
    if (dateFilter === "month") return tasksByDate;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (dateFilter === "today") {
      const iso = toIsoDate(now);
      return { [iso]: tasksByDate[iso] || [] };
    }

    if (dateFilter === "week") {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());

      const weekMap = {};

      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        const iso = toIsoDate(d);
        weekMap[iso] = tasksByDate[iso] || [];
      }

      return weekMap;
    }

    return tasksByDate;
  }, [tasksByDate, dateFilter]);

  const weekDaysForSelected = useMemo(() => {
    if (!selectedWeekInfo) return [];

    const start = new Date(selectedWeekInfo.start);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);

      days.push({
        date: d,
        iso: toIsoDate(d),
        label: d.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric"
        })
      });
    }

    return days;
  }, [selectedWeekInfo]);

  const prevMonth = () => {
    const [yStr, mStr] = monthValue.split("-");
    let y = Number(yStr);
    let m = Number(mStr) - 1;

    m -= 1;

    if (m < 0) {
      m = 11;
      y -= 1;
    }

    setMonthValue(`${y}-${String(m + 1).padStart(2, "0")}`);
    setDateFilter("month");
    setSelectedDateInfo(null);
    setSelectedWeekInfo(null);
  };

  const nextMonth = () => {
    const [yStr, mStr] = monthValue.split("-");
    let y = Number(yStr);
    let m = Number(mStr) - 1;

    m += 1;

    if (m > 11) {
      m = 0;
      y += 1;
    }

    setMonthValue(`${y}-${String(m + 1).padStart(2, "0")}`);
    setDateFilter("month");
    setSelectedDateInfo(null);
    setSelectedWeekInfo(null);
  };

  const openAddTaskForDate = (iso) => {
    setSelectedDateInfo(null);
    setSelectedWeekInfo(null);
    navigate(`/tasks/new?startDate=${iso}`);
  };

  const openEditTask = (task) => {
    if (task.is_preview) {
      showAlert(`This occurrence is scheduled for ${task.start_date}. It will become active on its start date.`);
      return;
    }
    const realTaskId = task.id;
    const fullTask = tasks.find((t) => t.id === realTaskId);

    if (fullTask) {
      setSelectedTaskDetails(fullTask);
      setSelectedDateInfo(null);
      setSelectedWeekInfo(null);
    }
  };

  const openDayModal = (md) => {
    setSelectedDateInfo(md);
  };

  const openTodayModal = () => {
    const now = new Date();
    const todayIso = toIsoDate(now);

    setDateFilter("today");
    setSelectedWeekInfo(null);

    setMonthValue(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    );

    setSelectedDateInfo({
      date: now,
      iso: todayIso,
      inMonth: true
    });
  };

  const openCurrentWeekModal = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());

    setSelectedDateInfo(null);
    setSelectedWeekInfo({
      start: weekStart,
      iso: toIsoDate(weekStart)
    });
  };

  const buttonStyle = (active = false) => ({
    background: active
      ? darkMode
        ? "#f9fafb"
        : "#fff"
      : "rgba(255,255,255,0.22)",
    color: active ? (darkMode ? "#4d7a22" : "#76AB3F") : "white",
    height: 42,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    border: "none",
    borderRadius: 14,
    padding: "0 18px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: active ? "0 3px 8px rgba(0,0,0,0.14)" : "none",
    flexShrink: 0
  });

  const canAddTask = isAdmin || isDeptAdmin || isUser;

  if (selectedTaskDetails) {
    return (
      <TaskDetailsPage
        task={selectedTaskDetails}
        currentUser={currentUser}
        users={users}
        teams={teams}
        subTeams={subTeams}
        darkMode={darkMode}
        onBack={() => setSelectedTaskDetails(null)}
        onEdit={() => {
          navigate(`/tasks/${selectedTaskDetails.id}?edit=true`);
        }}
        onDeleteTask={async (taskId, deleteSeries = false) => {
          if (onDeleteTask) {
            try {
              await onDeleteTask(taskId, deleteSeries);
              if (onRefreshTasks) await onRefreshTasks();
              return true;
            } catch (err) {
              console.error("Delete task failed:", err);
              return false;
            }
          }
          return false;
        }}
      />
    );
  }

  return (
    <div className={`page calendar-page ${darkMode ? "dark bg-gray-900" : ""}`}>
      <div
        className="page-header"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 16,
          marginBottom: 28,
          background: darkMode ? "#4d7a22" : "#76AB3F",
          padding: "24px 32px",
          borderRadius: 28,
          color: "white",
          boxShadow: darkMode
            ? "0 10px 25px -5px rgba(0,0,0,0.4)"
            : "0 10px 25px -5px rgba(16,185,129,0.25)",
          overflow: "visible",
          width: "100%"
        }}
      >
        <h2
          style={{
            color: "white",
            margin: 0,
            fontWeight: 800,
            fontSize: 28,
            lineHeight: 1.15,
            whiteSpace: "nowrap"
          }}
        >
          Calendar — {monthLabel}
        </h2>

        <div
          className="calendar-header-controls"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            overflow: "visible",
            paddingBottom: 2
          }}
        >
          {isAdmin || isDeptAdmin ? (
            <CustomSelect
              options={[
                { value: "all", label: "All Team Tasks" },
                ...teams
                  .filter((t) => {
                    if (isGlobalAdmin || isOperationalAdmin) return true;
                    if (isDeptAdmin) {
                      const adminDeptIds = currentUser?.adminDeptIds || [];
                      const allowedTeamIds = currentUser?.allowedTeamIds || [];
                      return adminDeptIds.includes(t.department_id) || allowedTeamIds.includes(t.id);
                    }
                    return false;
                  })
                  .map((t) => ({ value: t.id, label: t.name }))
              ]}
              value={filterScope === "all" ? "all" : selectedTeamId}
              onChange={(val) => {
                if (val === "all") {
                  setFilterScope("all");
                  setSelectedTeamId("");
                } else {
                  setFilterScope("team");
                  setSelectedTeamId(val);
                }
              }}
              style={{ minWidth: 170 }}
              darkMode={darkMode}
            />
          ) : (
            <button
              onClick={() => setFilterStatus("all")}
              style={buttonStyle(filterStatus === "all")}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  background:
                    filterStatus === "all"
                      ? darkMode
                        ? "#4d7a22"
                        : "#76AB3F"
                      : "white",
                  marginRight: 8
                }}
              />
              All Tasks
            </button>
          )}

          <div
            style={{
              width: 1,
              height: 26,
              background: "rgba(255,255,255,0.7)"
            }}
          />

          {["Todo", "In Progress", "Completed"].map((status) => (
            <button
              key={status}
              onClick={() =>
                setFilterStatus(filterStatus === status ? "all" : status)
              }
              style={buttonStyle(filterStatus === status)}
            >
              {status}
            </button>
          ))}

          <div
            style={{
              width: 1,
              height: 26,
              background: "rgba(255,255,255,0.7)"
            }}
          />

          <button onClick={openTodayModal} style={buttonStyle(dateFilter === "today")}>
            Today
          </button>

          <button
            onClick={() => {
              setDateFilter("week");
              openCurrentWeekModal();
            }}
            style={buttonStyle(dateFilter === "week")}
          >
            This Week
          </button>

          <div
            style={{
              width: 1,
              height: 26,
              background: "rgba(255,255,255,0.7)"
            }}
          />

          {isAdmin && (
            <input id="searchUser" name="searchUser"
              type="text"
              placeholder="Search user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: 150,
                height: 42,
                padding: "0 16px",
                borderRadius: 14,
                border: "none",
                fontFamily: "inherit",
                fontSize: 14,
                color: "white",
                outline: "none",
                background: "rgba(255,255,255,0.22)"
              }}
              className="calendar-search-input"
            />
          )}

          {canAddTask && (
            <button
              onClick={() => navigate("/tasks/new")}
              style={{
                ...buttonStyle(true),
                padding: "0 24px"
              }}
            >
              <span style={{ fontSize: 18, marginRight: 8 }}>+</span>
              New Task
            </button>
          )}

          <button onClick={prevMonth} aria-label="Previous month" style={buttonStyle(true)}>
            ◀
          </button>

          <input id="monthvalue" name="monthvalue"
            type="month"
            value={monthValue}
            onChange={(e) => {
              if (e.target.value) {
                setMonthValue(e.target.value);
                setDateFilter("month");
                setSelectedDateInfo(null);
                setSelectedWeekInfo(null);
              }
            }}
            aria-label="Pick month"
            style={{
              height: 42,
              width: 220,
              padding: "0 16px",
              borderRadius: 14,
              border: "none",
              fontFamily: "inherit",
              fontSize: 15,
              fontWeight: 700,
              color: darkMode ? "#4d7a22" : "#76AB3F",
              outline: "none",
              cursor: "pointer",
              background: darkMode ? "#f9fafb" : "white",
              boxShadow: "0 3px 8px rgba(0,0,0,0.14)"
            }}
          />

          <button onClick={nextMonth} aria-label="Next month" style={buttonStyle(true)}>
            ▶
          </button>
        </div>

        <style>
          {`
            .calendar-search-input::placeholder {
              color: rgba(255,255,255,0.75);
            }
          `}
        </style>

        <div
          style={{
            display: "flex",
            gap: 18,
            flexWrap: "wrap",
            fontSize: 15,
            color: "white"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#89f7b1ff" }} />
            Low
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#f0b88fff" }} />
            Medium
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fe6969ff" }} />
            High
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#3e81ecff" }} />
            Completed
          </div>
        </div>
      </div>

      <div className="calendar-wrapper" style={{ marginTop: 12, overflowX: "auto", width: "100%" }}>
        <div
          className="calendar-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(140px, 1fr))",
            gridTemplateRows: `auto repeat(${monthDates.length / 7}, minmax(155px, auto))`,
            gap: 12,
            minWidth: "900px",
            paddingBottom: 8
          }}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((wd, i) => (
            <div
              key={wd}
              style={{
                fontWeight: 700,
                padding: "6px 10px",
                gridRow: 1,
                gridColumn: i + 1,
                color: darkMode ? "#d1d5db" : "#374151",
                fontSize: 16
              }}
            >
              {wd}
            </div>
          ))}

          {monthDates.map((md, idx) => {
            const row = Math.floor(idx / 7) + 2;
            const col = (idx % 7) + 1;
            const dayTasks = visibleTasksByDate[md.iso] || [];

            return (
              <div
                key={md.iso}
                onClick={() => openDayModal(md)}
                style={{
                  gridRow: row,
                  gridColumn: col,
                  minHeight: 155,
                  maxHeight: 190,
                  overflowY: "auto",
                  borderRadius: 18,
                  padding: 10,
                  background: md.inMonth
                    ? darkMode
                      ? "#1f2937"
                      : "#ffffff"
                    : darkMode
                    ? "#111827"
                    : "#f3f4f6",
                  border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                  cursor: "pointer",
                  boxShadow: md.inMonth ? "0 1px 3px rgba(0,0,0,0.06)" : "none"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: md.inMonth
                        ? darkMode
                          ? "#f9fafb"
                          : "#111827"
                        : darkMode
                        ? "#6b7280"
                        : "#9ca3af"
                    }}
                  >
                    {md.date.getDate()}
                  </div>

                  {canAddTask && md.inMonth && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openAddTaskForDate(md.iso);
                      }}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 9,
                        border: "none",
                        background: "#76AB3F",
                        color: "white",
                        fontWeight: 900,
                        cursor: "pointer",
                        lineHeight: 1
                      }}
                    >
                      +
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {dayTasks.slice(0, 5).map((task) => (
                    <div
                      key={`${md.iso}-${task.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditTask(task);
                      }}
                      title={`${task.title}${task.is_loop_occurrence && (task.occurrence_number || task.occurrence_index !== undefined) ? ` #${task.occurrence_number || task.occurrence_index + 1}` : ""} | ${getAssigneeDisplay(task)}`}
                      style={{
                        background: task._color,
                        borderRadius: 12,
                        padding: "7px 9px",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        border:
                          task.progress_status === "Trouble"
                            ? "2px solid #ef4444"
                            : "none"
                      }}
                    >
                      {task.title}
                      {task.is_loop_occurrence && (task.occurrence_number || task.occurrence_index !== undefined) ? ` #${task.occurrence_number || task.occurrence_index + 1}` : ""}
                      <span style={{ opacity: 0.7, fontWeight: 400, fontSize: 10 }}>
                        {" "}
                        | {getAssigneeDisplay(task)}
                      </span>
                    </div>
                  ))}

                  {dayTasks.length > 5 && (
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: darkMode ? "#d1d5db" : "#6b7280",
                        padding: "2px 4px"
                      }}
                    >
                      +{dayTasks.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDateInfo && (
        <div
          onClick={() => setSelectedDateInfo(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(620px, 95vw)",
              maxHeight: "82vh",
              overflowY: "auto",
              background: darkMode ? "#1f2937" : "#ffffff",
              borderRadius: 22,
              padding: 24,
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 18
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: darkMode ? "#ffffff" : "#111827",
                  fontSize: 22,
                  fontWeight: 800
                }}
              >
                Tasks —{" "}
                {selectedDateInfo.date.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
              </h3>

              <div style={{ display: "flex", gap: 10 }}>
                {canAddTask && (
                  <button
                    onClick={() => openAddTaskForDate(selectedDateInfo.iso)}
                    style={{
                      background: "#76AB3F",
                      border: "none",
                      color: "white",
                      padding: "10px 16px",
                      borderRadius: 12,
                      cursor: "pointer",
                      fontWeight: 800
                    }}
                  >
                    + Add Task
                  </button>
                )}

                <button
                  onClick={() => setSelectedDateInfo(null)}
                  style={{
                    background: darkMode ? "#374151" : "#f3f4f6",
                    border: "none",
                    color: darkMode ? "#fff" : "#111827",
                    padding: "10px 14px",
                    borderRadius: 12,
                    cursor: "pointer",
                    fontWeight: 800
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {(tasksByDate[selectedDateInfo.iso] || []).length === 0 ? (
              <div
                style={{
                  padding: 22,
                  borderRadius: 16,
                  background: darkMode ? "#111827" : "#f9fafb",
                  color: darkMode ? "#d1d5db" : "#6b7280",
                  fontWeight: 700
                }}
              >
                No tasks for this day.
              </div>
            ) : (
              (tasksByDate[selectedDateInfo.iso] || []).map((task) => (
                <div
                  key={`modal-${task.id}`}
                  onClick={() => openEditTask(task)}
                  style={{
                    background: task._color,
                    padding: 14,
                    borderRadius: 16,
                    marginBottom: 12,
                    cursor: "pointer",
                    color: "#111827",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 5 }}>
                    {task.title}
                    {task.is_loop_occurrence && (task.occurrence_number || task.occurrence_index !== undefined) ? ` #${task.occurrence_number || task.occurrence_index + 1}` : ""}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
                    {getAssigneeDisplay(task)} • {task.status || "Todo"} •{" "}
                    {task.priority || "Medium"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {selectedWeekInfo && (
        <div
          onClick={() => setSelectedWeekInfo(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 96vw)",
              maxHeight: "84vh",
              overflowY: "auto",
              background: darkMode ? "#1f2937" : "#ffffff",
              borderRadius: 22,
              padding: 24,
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: darkMode ? "#ffffff" : "#111827",
                  fontSize: 22,
                  fontWeight: 800
                }}
              >
                This Week Tasks
              </h3>

              <button
                onClick={() => setSelectedWeekInfo(null)}
                style={{
                  background: darkMode ? "#374151" : "#f3f4f6",
                  border: "none",
                  color: darkMode ? "#fff" : "#111827",
                  padding: "10px 14px",
                  borderRadius: 12,
                  cursor: "pointer",
                  fontWeight: 800
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              {weekDaysForSelected.map((day) => {
                const dayTasks = tasksByDate[day.iso] || [];

                return (
                  <div
                    key={day.iso}
                    style={{
                      border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
                      background: darkMode ? "#111827" : "#f9fafb",
                      borderRadius: 18,
                      padding: 16
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          color: darkMode ? "#ffffff" : "#111827",
                          fontWeight: 800
                        }}
                      >
                        {day.label}
                      </h4>

                      {canAddTask && (
                        <button
                          onClick={() => openAddTaskForDate(day.iso)}
                          style={{
                            background: "#76AB3F",
                            border: "none",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: 10,
                            cursor: "pointer",
                            fontWeight: 800
                          }}
                        >
                          + Add
                        </button>
                      )}
                    </div>

                    {dayTasks.length === 0 ? (
                      <div
                        style={{
                          color: darkMode ? "#9ca3af" : "#6b7280",
                          fontWeight: 600,
                          fontSize: 13
                        }}
                      >
                        No tasks.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        {dayTasks.map((task) => (
                          <div
                            key={`week-${day.iso}-${task.id}`}
                            onClick={() => openEditTask(task)}
                            style={{
                              background: task._color,
                              padding: 12,
                              borderRadius: 14,
                              cursor: "pointer",
                              color: "#111827"
                            }}
                          >
                            <div style={{ fontWeight: 500 }}>
                              {task.title}
                              {task.is_loop_occurrence && (task.occurrence_number || task.occurrence_index !== undefined) ? ` #${task.occurrence_number || task.occurrence_index + 1}` : ""}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 4 }}>
                              {getAssigneeDisplay(task)} •{" "}
                              {task.status || "Todo"}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <TaskForm
          onSave={async (taskData) => {
            const now = new Date().toISOString();

            if (editingTask?.id) {
              const payload = {
                ...taskData,
                title: taskData.title,
                description: taskData.description,
                assignee: taskData.assignee,
                assignee_email: taskData.assignee_email,
                start_date:
                  taskData.start_date ||
                  (taskData.startDate
                    ? typeof taskData.startDate === "string"
                      ? taskData.startDate.slice(0, 10)
                      : new Date(taskData.startDate).toISOString().slice(0, 10)
                    : null),
                due_date:
                  taskData.due_date ||
                  (taskData.dueDate
                    ? typeof taskData.dueDate === "string"
                      ? taskData.dueDate.slice(0, 10)
                      : new Date(taskData.dueDate).toISOString().slice(0, 10)
                    : null),
                priority: taskData.priority,
                status: taskData.status,
                budget: taskData.budget,
                extended_due_date_1: taskData.extended_due_date_1 || null,
                extended_due_date_2: taskData.extended_due_date_2 || null,
                task_note: taskData.task_note || null,
                task_type: taskData.task_type || "regular",
                repeat_frequency: taskData.repeat_frequency || null,
                repeat_every: taskData.repeat_every || 1,
                repeat_start_date: taskData.repeat_start_date || null,
                repeat_end_date: taskData.repeat_end_date || null,
                repeat_occurrences: taskData.repeat_occurrences || null,
                repeat_days: taskData.repeat_days || [],

                loop_duration_value:
                  taskData.loop_duration_value ?? taskData.loopDurationValue ?? 1,

                loop_duration_unit:
                  taskData.loop_duration_unit ?? taskData.loopDurationUnit ?? "days",

                create_next_after_completion:
                  taskData.create_next_after_completion ??
                  taskData.createNextAfterCompletion ??
                  false,

                assignee_id: taskData.assignee_id || null,
                assignees: taskData.assignees || [],
                assigned_users: taskData.assigned_users || taskData.assignees || [],

                updated_at: now
              };

              try {
                await onUpdateTask(editingTask.id, payload);
              } catch (err) {
                console.error(err);
                showAlert("Failed to update task: " + (err?.message || err));
              }
            } else {
              const payload = {
                ...taskData,
                id: taskData.id || undefined,
                title: taskData.title || "Untitled",
                description: taskData.description || null,
                assignee: taskData.assignee || null,
                assignee_email: taskData.assignee_email || null,
                start_date:
                  taskData.start_date ||
                  (taskData.startDate
                    ? typeof taskData.startDate === "string"
                      ? taskData.startDate.slice(0, 10)
                      : new Date(taskData.startDate).toISOString().slice(0, 10)
                    : editingTask?.start_date || null),
                due_date:
                  taskData.due_date ||
                  (taskData.dueDate
                    ? typeof taskData.dueDate === "string"
                      ? taskData.dueDate.slice(0, 10)
                      : new Date(taskData.dueDate).toISOString().slice(0, 10)
                    : editingTask?.due_date || null),
                priority: taskData.priority || "Medium",
                status: taskData.status || "Todo",
                budget: taskData.budget,
                extended_due_date_1: taskData.extended_due_date_1 || null,
                extended_due_date_2: taskData.extended_due_date_2 || null,
                task_note: taskData.task_note || null,
                task_type: taskData.task_type || "regular",
                repeat_frequency: taskData.repeat_frequency || null,
                repeat_every: taskData.repeat_every || 1,
                repeat_start_date: taskData.repeat_start_date || null,
                repeat_end_date: taskData.repeat_end_date || null,
                repeat_occurrences: taskData.repeat_occurrences || null,
                repeat_days: taskData.repeat_days || [],

                loop_duration_value:
                  taskData.loop_duration_value ?? taskData.loopDurationValue ?? 1,

                loop_duration_unit:
                  taskData.loop_duration_unit ?? taskData.loopDurationUnit ?? "days",

                create_next_after_completion:
                  taskData.create_next_after_completion ??
                  taskData.createNextAfterCompletion ??
                  false,

                assignee_id: taskData.assignee_id || null,
                assignees: taskData.assignees || [],
                assigned_users: taskData.assigned_users || taskData.assignees || [],

                created_at: taskData.created_at || now,
                updated_at: now
              };

              try {
                const cleanAssignedUsers = [
                  ...new Map(
                    (taskData.assigned_users || taskData.assignees || [])
                      .filter((u) => u?.id)
                      .map((u) => [u.id, u]),
                  ).values(),
                ];

                const cleanUserCases = (taskData.user_cases || []).filter(
                  (uc) => uc.title || uc.description || uc.acceptanceCriteria,
                );

                const createdTask = await onCreateTask(payload);
                const createdTaskId = createdTask?.id || payload.id;

                console.log("createdTask returned:", createdTask);
                console.log("createdTaskId:", createdTaskId);
                console.log("cleanAssignedUsers before child insert:", cleanAssignedUsers);

                await addTaskChildrenAndNotify({
                  taskId: createdTaskId,
                  payload,
                  cleanAssignedUsers,
                  cleanUserCases,
                });

                if (onRefreshTasks) await onRefreshTasks();
              } catch (err) {
                console.error(err);
                showAlert("Failed to create task: " + (err?.message || err));
              }
            }

            setIsModalOpen(false);
            setEditingTask(null);
          }}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
          onRefreshUsers={onRefreshUsers}
          users={users}
          currentUser={currentUser}
          initial={editingTask}
          readOnly={false}
          allowMainEditing={
            !editingTask?.id
              ? isGlobalAdmin || isOperationalAdmin || isDeptAdmin || isUser
              : isGlobalAdmin ||
                isOperationalAdmin ||
                (isDeptAdmin && isTaskInMyTeam(editingTask))
          }
          teams={teams}
          subTeams={subTeams}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}

export default CalendarView;
